import {
    AuthenticationActions,
    AuthenticationState,
    debugExtendedAccess,
    getLoginUrl,
    tokenExchangePath,
} from '@news-mono/web-common'
import axios from 'axios'
import { PromiseCompletionSource } from 'promise-completion-source'
import { Dispatch } from 'react'
import { Logger } from 'typescript-log'

interface GaaMeteringRegwall {
    show(options: { iframeUrl: string }): Promise<any>
}

export interface GoogleEntitlementApi {
    enablesThisWithGoogleMetering: () => boolean
    enablesThis: () => boolean /* ... with Subscribe with Google */
    consume: (callback: () => void) => void
}

interface SubscriptionResponse {
    raw: string
    purchaseData: {
        raw: string
        signature: string
    }
    productType: 'SUBSCRIPTION'
    userData: {
        idToken: string
        data: {}
        id: string
        email: string
        emailVerified: boolean
        name: string
        givenName: string
        familyName: string
        pictureUrl: string
    }
    entitlements: {
        service: 'subscribe.google.com'
        entitlements: [
            {
                source: 'google'
                products: [string]
                subscriptionToken: string
            },
        ]
        isReadyToPay: boolean
    }
}

type GoogleExtendedAccessEvents =
    | 'EVENT_SHOWCASE_UNLOCKED_FREE_PAGE'
    | 'EVENT_SHOWCASE_UNLOCKED_BY_METER'
    | 'EVENT_SHOWCASE_UNLOCKED_BY_SUBSCRIPTION'
    | 'EVENT_SHOWCASE_NO_ENTITLEMENTS_PAYWALL'

export interface Subscriptions {
    setOnNativeSubscribeRequest: any
    setOnLoginRequest: (arg: () => void) => void
    setOnEntitlementsResponse: (
        arg: (entitlementsPromise: Promise<GoogleEntitlementApi>) => void,
    ) => void
    init: (name: string) => void
    getEntitlements: (args: {}) => void
    setShowcaseEntitlement: (args: {
        entitlement: GoogleExtendedAccessEvents
        isUserRegistered: boolean
    }) => void
}

declare global {
    interface Window {
        SWG: Array<(subscriptions: any) => void>
    }
}

declare const GaaMeteringRegwall: GaaMeteringRegwall

// Launch Google registration intervention when an anonymous user does not have access to read
// an article
export function showGoogleRegwall(
    dispatch: Dispatch<any>,
    authState: AuthenticationState,
    logger: Logger,
    subscriptionsPromise: Promise<any>,
) {
    const currentUrl = new URL(document.URL)
    GaaMeteringRegwall.show({
        // Specify a URL that renders a Google Sign-In button.
        // See the Google Sign-In widget section.
        iframeUrl: `${currentUrl.origin}/extended-access-login`,
    }).then((userData) => {
        dispatch({
            type: AuthenticationActions.GOOGLE_LOGIN,
            payload: { googleLogin: 'linking' },
        })
        // Register a user based on data from Google Sign-In.
        // Definition of googleSignInUser: https://git.io/Jkyzm
        // See Authenticate with a backend server.
        registerUser(userData, dispatch, authState, logger).then(
            async (userState) => {
                // Create userState object for newly-registered user and send it to Google
                // See the Entitlements sync flow section.
                const { subscriptions } = await subscriptionsPromise
                debugExtendedAccess('Setting user state %O', userState)
                subscriptions.getEntitlements(userState)
            },
        )
    })
}

async function registerUser(
    userData: any,
    dispatch: Dispatch<any>,
    authState: AuthenticationState,
    logger: Logger,
) {
    return new Promise((resolve) => {
        debugExtendedAccess('Exchanging google token for auth tokens')
        const data = {
            token: userData.idToken,
        }
        axios
            .post(tokenExchangePath, data)
            .then(function (response) {
                debugExtendedAccess('Token exchange complete %O', response.data)
                dispatch({
                    type: AuthenticationActions.GOOGLE_LOGIN,
                    payload: { googleLogin: 'complete' },
                })
                resolve(response.data)
            })
            .catch(function (err) {
                logger.error({ err }, 'Token exchange failed')
            })
    })
}

export function initGoogleExtendedAccess(subscribeLink: string): Promise<{
    subscriptions: Subscriptions
    entitlementsPromise: Promise<GoogleEntitlementApi>
}> {
    return new Promise((resolve) => {
        ;(self.SWG = self.SWG || []).push(function (
            subscriptions: Subscriptions,
        ) {
            debugExtendedAccess('Initialising extended access')
            // Init manually https://github.com/subscriptions-project/swg-js/blob/main/docs/embed-client.md#manual-initialization
            const publicationOrProductId = `${
                window.location.hostname.match(/(localhost|swmdigital\.io)$/)
                    ? 'thewest.com.au'
                    : window.location.hostname.replace(/www\./, '')
            }:showcase`
            subscriptions.init(publicationOrProductId)

            // Handle when the user clicks "Already have an account?"
            subscriptions.setOnLoginRequest(() => {
                document.location.href = getLoginUrl()
            })

            // Handle when the user clicks "Subscribe"
            subscriptions.setOnNativeSubscribeRequest(() => {
                document.location.href = subscribeLink
            })

            const GoogleEntitlementPromise =
                new PromiseCompletionSource<GoogleEntitlementApi>()
            subscriptions.setOnEntitlementsResponse(
                (entitlementsPromise: Promise<GoogleEntitlementApi>) => {
                    entitlementsPromise.then((googleEntitlement) => {
                        // Prob need to have some sort of timeout/error handling?
                        GoogleEntitlementPromise.resolve(googleEntitlement)
                    })
                },
            )

            resolve({
                subscriptions,
                entitlementsPromise: GoogleEntitlementPromise.promise,
            })
        })
    })
}
